import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ShareButton } from "@/components/ShareButton";
import { BringAFriendDialog } from "@/components/BringAFriendDialog";
import { WelcomeOrientation } from "@/components/WelcomeOrientation";
import { ChecklistCompleteDialog } from "@/components/ChecklistCompleteDialog";
import { toast } from "sonner";
import { Medal, Award, Trophy, Send, UserPlus } from "lucide-react";

const CONFETTI_COLORS = ["#D36135", "#1A4331", "#B8860B", "#9CA3AF", "#E8B04B", "#A0522D"];

// Pure CSS confetti (no deps). Hidden entirely under prefers-reduced-motion —
// both here (never rendered) and in CSS (animation disabled) for safety.
const Confetti = () => {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return null;
  return (
    <div data-testid="celebration-confetti" className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {Array.from({ length: 30 }, (_, i) => (
        <span
          key={i} // index key OK: fixed-length generated array, never reordered
          className="confetti-piece"
          style={{
            left: `${(i * 37 + 11) % 100}%`,
            backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${(i % 12) * 0.14}s`,
            animationDuration: `${2.1 + (i % 5) * 0.4}s`,
          }}
        />
      ))}
    </div>
  );
};

const KIND_META = {
  tier: { kicker: "Tier promotion", icon: Medal },
  badge: { kicker: "New badge", icon: Award },
  award: { kicker: "New award", icon: Trophy },
};

// Celebration moments (Phase 13): shows one uncelebrated achievement at a
// time (tiers first, then badges, then awards — server orders the queue).
// Each item is acked the moment it is DISPLAYED, so a reload never re-fires.
export const CelebrationHost = () => {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [collectives, setCollectives] = useState(null);
  const [target, setTarget] = useState("");
  const [posting, setPosting] = useState(false);
  const [refInfo, setRefInfo] = useState(null); // null = not fetched yet
  const acked = useRef(new Set());

  useEffect(() => {
    api.myCelebrations().then((d) => setQueue(d?.items || [])).catch(() => {});
  }, []);

  const current = queue[0] || null;
  const isTier = current?.kind === "tier";

  // referral link for the invite button — the backend lazily mints a code for
  // every authenticated user, so this only comes back empty on API failure
  // (button hides gracefully in that case)
  useEffect(() => {
    if (!current || refInfo !== null) return;
    api.myReferral().then((d) => setRefInfo(d || {})).catch(() => setRefInfo({}));
  }, [current, refInfo]);

  useEffect(() => {
    if (current && !acked.current.has(current.id)) {
      acked.current.add(current.id);
      api.ackCelebrations([current.id]).catch(() => {});
    }
  }, [current]);

  // tier celebrations offer post-to-collective — load memberships once
  useEffect(() => {
    if (!isTier || collectives !== null) return;
    api.myCollectives()
      .then((d) => {
        const list = d?.collectives || [];
        setCollectives(list);
        if (list.length) setTarget(list[0].id);
      })
      .catch(() => setCollectives([]));
  }, [isTier, collectives]);

  if (!current) return null;

  const dismiss = () => setQueue((q) => q.slice(1));

  // "Bring a friend" invite prompt rides the same one-at-a-time queue — the
  // server appends it AFTER fresh achievements, so it never stacks with a
  // celebration. The dialog acks itself (idempotent with the effect above).
  if (current.kind === "invite") {
    return <BringAFriendDialog onClose={dismiss} />;
  }

  // First-time orientation (kind "welcome") — the server puts it FIRST in
  // the queue, so a brand-new user always meets it before any celebration.
  // Already acked-on-display by the effect above (once per user ever).
  if (current.kind === "welcome") {
    return <WelcomeOrientation user={user} onClose={dismiss} />;
  }

  // Checklist-complete closure (server-ordered after the achievement that
  // completed the final step, before the invite prompt). Gentle by design.
  if (current.kind === "checklist_complete") {
    return <ChecklistCompleteDialog onClose={dismiss} />;
  }

  const meta = KIND_META[current.kind] || KIND_META.badge;
  const cardUrl = api.ogAchievementUrl(user.user_id, current.id);

  const post = async () => {
    if (!target) return;
    setPosting(true);
    try {
      await api.shareAchievementToCollective(target, current.id);
      const c = collectives.find((x) => x.id === target);
      toast.success(`Shared to ${c?.name || "your collective"}'s noticeboard`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share to the noticeboard");
    }
    setPosting(false);
  };

  const invite = async () => {
    const url = refInfo?.referral_url;
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on CivicHearth",
          text: "I'm volunteering on CivicHearth — join me and we'll both watch the impact add up.",
          url,
        });
        api.trackReferralShare("celebration").catch(() => {});
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user closed the sheet
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied — send it to a friend");
      api.trackReferralShare("celebration").catch(() => {});
    } catch {
      toast.error("Couldn't copy the invite link");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent data-testid="celebration-dialog" className="sm:max-w-lg overflow-hidden">
        <Confetti />
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-bold flex items-center gap-1.5">
            <meta.icon className="w-4 h-4" /> {meta.kicker}
          </div>
          <DialogTitle data-testid="celebration-title" className="font-display text-3xl text-forest tracking-tight">
            {isTier ? `You've reached ${current.label}!` : `${current.label} unlocked!`}
          </DialogTitle>
          <DialogDescription data-testid="celebration-desc">{current.desc}</DialogDescription>
        </DialogHeader>

        <img
          src={cardUrl}
          alt={`${current.label} achievement card`}
          data-testid="celebration-card-img"
          className="w-full rounded-xl border border-forest/10"
        />

        {isTier && collectives?.length > 0 && (
          <div className="flex gap-2">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="celebration-collective-select" className="flex-1 rounded-full h-10 text-sm">
                <SelectValue placeholder="Pick a collective" />
              </SelectTrigger>
              <SelectContent>
                {collectives.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="celebration-post-btn" onClick={post} disabled={posting || !target} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-5">
              <Send className="w-4 h-4 mr-1.5" /> Post
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-2">
          <ShareButton
            testId="celebration-share-btn"
            imageUrl={cardUrl}
            shareUrl={`${window.location.origin}/u/${user.user_id}`}
            title={isTier ? `I just hit ${current.label} on CivicHearth` : `${current.label} — CivicHearth`}
            text={isTier
              ? `I just reached the ${current.label} volunteer tier on CivicHearth.`
              : `I just earned "${current.label}" on CivicHearth — ${current.desc}`}
            filename="civichearth-achievement.png"
            label="Share"
          />
          <div className="flex items-center gap-3">
            {queue.length > 1 && (
              <span className="text-xs text-neutral-400">{queue.length - 1} more to celebrate</span>
            )}
            <Button data-testid="celebration-dismiss-btn" variant="outline" onClick={dismiss} className="rounded-full">
              {queue.length > 1 ? "Next" : "Keep going"}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 -mt-1">
          {refInfo?.referral_url ? (
            <button
              type="button"
              data-testid="celebration-invite-btn"
              onClick={invite}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-terracotta hover:underline"
            >
              <UserPlus className="w-3.5 h-3.5" /> Invite a friend to join me
            </button>
          ) : <span />}
          <Link to="/recognition?tab=badges" onClick={dismiss} data-testid="celebration-badges-link" className="text-xs text-forest hover:underline">
            See all your badges
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};
